'use client';

// Composer speech context: one interactive session (store + controller) per
// mounted composer, the insert target and the reason the microphone is
// unavailable, if any. There is no language pick: engines detect the spoken
// language, with the company's working languages as silent server-side hints.
//
// Availability is honest: the mic is disabled, with the reason, when the
// server has no speech provider, the origin is insecure, the browser cannot
// capture, or a meeting holds the microphone. No path records and then fails
// for a reason known in advance.
import type { SpeechCopyKey } from '@/i18n/ns/speech';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type PropsWithChildren,
} from 'react';
import { useStore } from 'zustand';
import { captureSupport, startCapture } from '../capture/core';
import { micOwner } from '../capture/mic-owner';
import type { MicKind, MicOwner, SpeechAvailability, SpeechConfig } from '../contracts';
import { createDictationController, type DictationController } from '../interactive/session';
import { createSpeechStore, type SpeechState, type SpeechStore } from '../interactive/store';
import { speechSpool, SPOOL_TTL_MS } from '../spool';
import { fetchSpeechConfig, mintSpeechSession } from '../transport/http';
import { SpeechSocket } from '../transport/session-socket';
import { useVoiceConfig, voiceConfigStore } from '@/voice/state';
import { guardCaptureStart } from '../policy-guard';

export type SpeechContextValue = {
  availability: SpeechAvailability;
  store: SpeechStore;
  controller: DictationController;
  /** The composer registers how committed text reaches the caret. */
  registerInsert(insert: (text: string) => boolean): () => void;
  /** Why the microphone cannot start, as a copy key; null when it can. */
  blocked: SpeechCopyKey | null;
};

export const SpeechContext = createContext<SpeechContextValue | null>(null);

export const useSpeech = (): SpeechContextValue | null => useContext(SpeechContext);

const IDLE_STORE = createSpeechStore();

/**
 * Register how committed speech reaches the caret. The latest callback is
 * used; there is one target per mounted composer.
 */
export function useSpeechInsertTarget(insert: (text: string) => boolean): void {
  const register = useSpeech()?.registerInsert;
  const latest = useRef(insert);
  latest.current = insert;
  useEffect(() => register?.((text) => latest.current(text)), [register]);
}

/** A slice of the speech state; the idle state when no provider is mounted. */
export function useSpeechState<T>(selector: (state: SpeechState) => T): T {
  return useStore(useSpeech()?.store ?? IDLE_STORE, selector);
}

/** The reason the microphone is disabled, or null. Exported for tests. */
export function blockedReason(
  availability: SpeechAvailability,
  support: { mic: boolean; secure: boolean },
  micKind: MicKind | null,
  signedIn: boolean,
): SpeechCopyKey | null {
  if (!signedIn) return 'signInAgain';
  if (!availability.available || !availability.modes.dictation)
    return availability.reason === 'not_configured' ? 'notConfigured' : 'unavailable';
  if (!support.secure) return 'insecureContext';
  if (!support.mic) return 'unsupported';
  if (micKind === 'meeting') return 'micHeldByMeeting';
  return null;
}

let swept = false;

const newId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `sp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

function useMicKind(owner: MicOwner): MicKind | null {
  return useSyncExternalStore(owner.subscribe, owner.current, () => null);
}

export function SpeechProvider({
  availability,
  userId,
  threadId = null,
  organizationId = null,
  children,
}: PropsWithChildren<{
  availability: SpeechAvailability;
  userId: string | null;
  threadId?: string | null;
  organizationId?: string | null;
}>) {
  const store = useMemo(() => createSpeechStore(), []);
  // The session limits from GET /api/v1/speech/config, read at the next start.
  const voiceConfig = useVoiceConfig();
  const config = useRef<SpeechConfig | null>(null);
  const scope = useRef(`${userId ?? ''}:${organizationId ?? ''}`);
  scope.current = `${userId ?? ''}:${organizationId ?? ''}`;
  const insertRef = useRef<((text: string) => boolean) | null>(null);
  const live = useRef({ userId, threadId });
  live.current = { userId, threadId };

  const controller = useMemo(
    () =>
      createDictationController({
        store,
        micOwner,
        spool: speechSpool,
        startCapture: guardCaptureStart(
          fetchSpeechConfig,
          () => scope.current,
          (options, deps) => startCapture(options, deps),
        ),
        mint: (request) => mintSpeechSession(request),
        openSocket: (options) => new SpeechSocket(options),
        timers: {
          setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
          clearTimeout: (handle) => globalThis.clearTimeout(handle as number),
        },
        now: () => Date.now(),
        online: () => globalThis.navigator?.onLine !== false,
        onOnline: (callback) => {
          globalThis.addEventListener?.('online', callback);
          return () => globalThis.removeEventListener?.('online', callback);
        },
        newId,
        owner: () => live.current.userId,
        threadId: () => live.current.threadId,
        limits: () => config.current?.limits ?? null,
        insert: (text) => insertRef.current?.(text) ?? false,
      }),
    [store],
  );
  useEffect(() => () => controller.suspend(), [controller]);

  useEffect(() => {
    scope.current = `${userId ?? ''}:${organizationId ?? ''}`;
    voiceConfigStore.reset();
    if (userId) void voiceConfigStore.refresh(`${userId}:${organizationId ?? ''}`);
    return () => {
      scope.current = '';
      voiceConfigStore.reset();
    };
  }, [userId, organizationId]);

  useEffect(() => {
    config.current = voiceConfig.speech;
  }, [voiceConfig.speech]);

  useEffect(() => {
    if (!userId) return;
    const sweep = swept ? Promise.resolve(0) : speechSpool.sweep(SPOOL_TTL_MS).catch(() => 0);
    swept = true;
    void sweep.then(() => controller.restoreKept());
  }, [userId, controller]);

  const registerInsert = useCallback((insert: (text: string) => boolean) => {
    insertRef.current = insert;
    return () => {
      if (insertRef.current === insert) insertRef.current = null;
    };
  }, []);

  const micKind = useMicKind(micOwner);
  const scopedAvailability = voiceConfig.speech
    ? {
        ...availability,
        available: availability.available && voiceConfig.speech.available,
        reason: voiceConfig.speech.available
          ? availability.reason
          : (voiceConfig.speech.reason ?? 'unknown'),
        modes: {
          ...availability.modes,
          dictation: availability.modes.dictation && voiceConfig.speech.available,
        },
      }
    : {
        ...availability,
        modes: { ...availability.modes, dictation: false },
        reason: 'unknown' as const,
      };
  const blocked = blockedReason(scopedAvailability, captureSupport(), micKind, Boolean(userId));
  const value = useMemo<SpeechContextValue>(
    () => ({ availability: scopedAvailability, store, controller, registerInsert, blocked }),
    [scopedAvailability, store, controller, registerInsert, blocked],
  );
  return <SpeechContext.Provider value={value}>{children}</SpeechContext.Provider>;
}
